import React from 'react';

import { useTranslation } from '@/i18n';
import { SidePanelFormLayout } from '@/components/layout';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { Selector } from '@/components/ui/inputs/selector';
import { FilterChip } from '@/components/ui/filters';
import { Text } from '@/components/ui/text/Text';
import { IconScout } from '@/components/ui/images/IconScout';
import { colors } from '@/assets/styles/colors';
import type { ScheduleOption } from '@/api/domains/shift';
import type { SelectorOption } from '@/components/ui/inputs/selector/Selector.types';
import {
  useAssignScheduleFormPanel,
  type AssignScheduleReplicableDay,
  type AssignScheduleExistingAssignment,
  type AssignScheduleResult,
} from '@/hooks/shift/useAssignScheduleFormPanel';


interface AssignScheduleFormPanelProps {
  replicableDays: AssignScheduleReplicableDay[];
  existingAssignment?: AssignScheduleExistingAssignment;
  /** ASIS-5051 (perf): catalog loaded once by the parent, see useAssignScheduleFormPanel */
  schedules: ScheduleOption[];
  scheduleOptions: SelectorOption[];
  isSchedulesLoaded: boolean;
  onAssign: (result: AssignScheduleResult) => void;
  onClear?: () => void;
}

export const AssignScheduleFormPanel: React.FC<AssignScheduleFormPanelProps> = ({
  replicableDays,
  existingAssignment,
  schedules,
  scheduleOptions,
  isSchedulesLoaded,
  onAssign,
  onClear,
}) => {
  const { t: translate } = useTranslation('schedule');

  const {
    formSubset,
    isLoadingSchedules,
    hasSecondSchedule,
    schedule1Details,
    schedule2Details,
    selectedReplicateDays,
    isReady,
    isEditMode,
    addSecondSchedule,
    removeSecondSchedule,
    toggleReplicateDay,
    handleSubmit,
    handleClear,
  } = useAssignScheduleFormPanel({
    replicableDays,
    existingAssignment,
    schedules,
    scheduleOptions,
    isSchedulesLoaded,
    onAssign,
    onClear,
  });

  const { watch, setValue, errors } = formSubset;

  return (
    <SidePanelFormLayout data-testid="assign-schedule-form-panel">
      <SidePanelFormLayout.Fields>
        <Selector
          label={translate('shift.create.assignSchedule.fields.schedule1')}
          required
          disabled={isLoadingSchedules}
          options={scheduleOptions}
          value={watch('schedule1Id') != null ? String(watch('schedule1Id')) : undefined}
          onChange={(value) => {
            const parsed = Array.isArray(value) ? value[0] : value;
            setValue('schedule1Id', parsed ? Number(parsed) : null, { shouldDirty: true, shouldValidate: true });
          }}
          color={errors.schedule1Id ? 'error' : 'default'}
          supportingText={
            errors.schedule1Id?.message ? translate(errors.schedule1Id.message) : undefined
          }
          data-testid="assign-schedule-schedule1-selector"
        />

        {schedule1Details && (
          <div className="grid grid-cols-3 gap-5" data-testid="assign-schedule-schedule1-details">
            <div className="rounded-[10px] px-2 py-2" style={{ backgroundColor: colors.scheduleDetail.detailBackground }}>
              <Text variant="small-regular" lineHeight="20px" color={colors.scheduleDetail.detailLabel} style={{ fontSize: '10px', fontWeight: 500 }}>
                {translate('shift.create.assignSchedule.fields.entryTime')}
              </Text>
              <Text variant="subheader-medium" lineHeight="20px" color={colors.scheduleDetail.entryTimeValue}>
                {schedule1Details.startTime}
              </Text>
            </div>
            <div className="rounded-[10px] px-2 py-2" style={{ backgroundColor: colors.scheduleDetail.detailBackground }}>
              <Text variant="small-regular" lineHeight="20px" color={colors.scheduleDetail.detailLabel} style={{ fontSize: '10px', fontWeight: 500 }}>
                {translate('shift.create.assignSchedule.fields.exitTime')}
              </Text>
              <Text variant="subheader-medium" lineHeight="20px" color={colors.scheduleDetail.exitTimeValue}>
                {schedule1Details.endTime}
              </Text>
            </div>
            <div className="rounded-[10px] px-2 py-2" style={{ backgroundColor: colors.scheduleDetail.detailBackground }}>
              <Text variant="small-regular" lineHeight="20px" color={colors.scheduleDetail.detailLabel} style={{ fontSize: '10px', fontWeight: 500 }}>
                {translate('shift.create.assignSchedule.fields.break')}
              </Text>
              <Text variant="subheader-medium" lineHeight="20px" color={colors.scheduleDetail.breakValue}>
                {translate('shift.create.assignSchedule.fields.breakMinutes', { minutes: schedule1Details.breakMinutes })}
              </Text>
            </div>
          </div>
        )}

        {!hasSecondSchedule && (
          <PrimaryButton
            variant="outline"
            fullWidth
            textVariant="subheader-medium"
            onClick={addSecondSchedule}
            customVariant={{
              default: {
                backgroundColor: colors.scheduleDetail.addButtonBackground,
                borderColor: colors.scheduleDetail.addButtonBorder,
                textColor: colors.scheduleDetail.addButtonText,
              },
              hover: {
                backgroundColor: colors.scheduleDetail.addButtonBackground,
                borderColor: colors.scheduleDetail.addButtonAccentHover,
                textColor: colors.scheduleDetail.addButtonAccentHover,
              },
              pressed: {
                backgroundColor: colors.scheduleDetail.addButtonBackground,
                borderColor: colors.scheduleDetail.addButtonAccentPressed,
                textColor: colors.scheduleDetail.addButtonAccentPressed,
              },
              focus: {
                backgroundColor: colors.scheduleDetail.addButtonBackground,
                borderColor: colors.scheduleDetail.addButtonBorder,
                textColor: colors.scheduleDetail.addButtonText,
              },
            }}
            data-testid="assign-schedule-add-second-button"
          >
            {translate('shift.create.assignSchedule.actions.addSchedule')}
          </PrimaryButton>
        )}

        {hasSecondSchedule && (
          <>
            <div className="flex items-center justify-between">
              <Text variant="subheader-medium" color={colors.primary.dash}>
                {translate('shift.create.assignSchedule.fields.schedule2')}
              </Text>
              <button
                type="button"
                onClick={removeSecondSchedule}
                aria-label={translate('common:actions.delete')}
                data-testid="assign-schedule-remove-second-button"
              >
                <IconScout name="close" color={colors.feedback.error300} size="sm" />
              </button>
            </div>

            <Selector
              label={translate('shift.create.assignSchedule.fields.schedule2')}
              disabled={isLoadingSchedules}
              options={scheduleOptions}
              value={watch('schedule2Id') != null ? String(watch('schedule2Id')) : undefined}
              onChange={(value) => {
                const parsed = Array.isArray(value) ? value[0] : value;
                setValue('schedule2Id', parsed ? Number(parsed) : null, { shouldDirty: true });
              }}
              data-testid="assign-schedule-schedule2-selector"
            />

            {schedule2Details && (
              <div className="grid grid-cols-3 gap-5" data-testid="assign-schedule-schedule2-details">
                <div className="rounded-[10px] px-2 py-2" style={{ backgroundColor: colors.scheduleDetail.detailBackground }}>
                  <Text variant="small-regular" lineHeight="20px" color={colors.scheduleDetail.detailLabel} style={{ fontSize: '10px', fontWeight: 500 }}>
                    {translate('shift.create.assignSchedule.fields.entryTime')}
                  </Text>
                  <Text variant="subheader-medium" lineHeight="20px" color={colors.scheduleDetail.entryTimeValue}>
                    {schedule2Details.startTime}
                  </Text>
                </div>
                <div className="rounded-[10px] px-2 py-2" style={{ backgroundColor: colors.scheduleDetail.detailBackground }}>
                  <Text variant="small-regular" lineHeight="20px" color={colors.scheduleDetail.detailLabel} style={{ fontSize: '10px', fontWeight: 500 }}>
                    {translate('shift.create.assignSchedule.fields.exitTime')}
                  </Text>
                  <Text variant="subheader-medium" lineHeight="20px" color={colors.scheduleDetail.exitTimeValue}>
                    {schedule2Details.endTime}
                  </Text>
                </div>
                <div className="rounded-[10px] px-2 py-2" style={{ backgroundColor: colors.scheduleDetail.detailBackground }}>
                  <Text variant="small-regular" lineHeight="20px" color={colors.scheduleDetail.detailLabel} style={{ fontSize: '10px', fontWeight: 500 }}>
                    {translate('shift.create.assignSchedule.fields.break')}
                  </Text>
                  <Text variant="subheader-medium" lineHeight="20px" color={colors.scheduleDetail.breakValue}>
                    {translate('shift.create.assignSchedule.fields.breakMinutes', { minutes: schedule2Details.breakMinutes })}
                  </Text>
                </div>
              </div>
            )}
          </>
        )}

        {replicableDays.length > 0 && (
          <div
            className="rounded-[10px] px-2 py-2"
            style={{ backgroundColor: colors.scheduleDetail.detailBackground }}
            data-testid="assign-schedule-replicate-section"
          >
            <Text variant="small-regular" lineHeight="20px" color={colors.scheduleDetail.detailLabel} style={{ fontSize: '10px', fontWeight: 500 }}>
              {translate('shift.create.assignSchedule.replicateSection')}
            </Text>
            <div className="flex flex-wrap gap-2 mt-2">
              {replicableDays.map((day) => (
                <FilterChip
                  key={day.daySequence}
                  label={day.label}
                  isActive={selectedReplicateDays.includes(day.daySequence)}
                  onClick={() => toggleReplicateDay(day.daySequence)}
                  showChevron={false}
                  minWidth={0}
                  activeBackgroundColor={colors.scheduleDetail.replicateChipActiveBackground}
                  activeTextColor={colors.scheduleDetail.addButtonText}
                  inactiveBackgroundColor={colors.grays.neutralCC}
                  inactiveTextColor={colors.scheduleDetail.replicateChipInactiveText}
                  labelStyle={{ fontSize: '10px', fontWeight: 500 }}
                />
              ))}
            </div>
          </div>
        )}
      </SidePanelFormLayout.Fields>

      <SidePanelFormLayout.Footer>
        <div className="flex gap-3 w-full">
          {isEditMode && (
            <PrimaryButton
              variant="outline"
              fullWidth
              onClick={handleClear}
              data-testid="assign-schedule-clear-button"
            >
              {translate('shift.create.assignSchedule.actions.clear')}
            </PrimaryButton>
          )}
          <PrimaryButton
            fullWidth
            disabled={!isReady}
            onClick={handleSubmit}
            data-testid="assign-schedule-save-button"
          >
            {translate('common:actions.save')}
          </PrimaryButton>
        </div>
      </SidePanelFormLayout.Footer>
    </SidePanelFormLayout>
  );
};

export default AssignScheduleFormPanel;
